import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { format, isAfter } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { IntlShape } from 'react-intl';
import { utcToZonedTime } from '../../../../../../infrastructure/utils/date-fns-overrides.utils';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { DateFnsFormats, TimeFormats } from '../../../../../../infrastructure/date-formatter';
import {
    GO_LIVE_DATE_TIME_REQUIRED_TEXT,
    GO_LIVE_AFTER_START_TIME_TEXT
} from './meeting-go-live-date.texts';

class MeetingGoLiveDateVM {
    @mobx.observable
    private isEnabled: boolean = false;
    @mobx.observable
    private goLiveDate: Date | null = null;
    @mobx.observable
    private goLiveTime: Date | null = null;
    @mobx.observable
    private meetingDate: Date | null = null;
    @mobx.observable
    private startDate: Date | null = null;
    @mobx.observable
    private timezone: string = '';

    constructor(
        initialGoLiveDateTime: Date | null,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider)
    ) {
        this.init(initialGoLiveDateTime);
    }

    private get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get enabled(): boolean {
        return this.isEnabled;
    }

    public get selectedDate(): Date | null {
        return this.goLiveDate;
    }

    public get selectedTime(): Date | null {
        return this.goLiveTime;
    }

    @mobx.computed
    public get goLiveDateTime(): Date | null {
        if (!this.isEnabled || !this.goLiveDate || !this.goLiveTime || !this.timezone) {
            return null;
        }
        return this.combineDateAndTime(this.goLiveDate, this.goLiveTime);
    }

    public getValidationError(): string | null {
        if (!this.isEnabled) {
            return null;
        }

        if (!this.goLiveDate || !this.goLiveTime) {
            return this.intl.formatMessage(GO_LIVE_DATE_TIME_REQUIRED_TEXT);
        }

        if (this.startDate && this.goLiveDateTime && isAfter(this.goLiveDateTime, this.startDate)) {
            return this.intl.formatMessage(GO_LIVE_AFTER_START_TIME_TEXT);
        }

        return null;
    }

    public hasValidationError(): boolean {
        return !!this.getValidationError();
    }

    public getMixPanelValue(): string {
        if (!this.isEnabled || !this.goLiveDateTime) {
            return 'Disabled';
        }
        const zonedDate = utcToZonedTime(this.goLiveDateTime, this.timezone);
        const formattedDate = format(zonedDate, 'dd MMM yyyy HH:mm');
        return `Enabled - ${formattedDate}`;
    }

    @mobx.action
    public toggleEnabled = (): void => {
        this.isEnabled = !this.isEnabled;
        this.prefillDateTimeWhenEnable();
        this.clearDateTimeWhenDisable();
    };

    @mobx.action
    public setGoLiveDate = (date: Date | null): void => {
        this.goLiveDate = date;
    };

    @mobx.action
    public setGoLiveTime = (date: Date | null): void => {
        this.goLiveTime = date;
    };

    @mobx.action
    public clearGoLiveTime = (): void => {
        this.goLiveTime = null;
    };

    @mobx.action
    public setMeetingDate = (date: Date | null): void => {
        this.meetingDate = date;
    };

    @mobx.action
    public setStartDate = (date: Date | null): void => {
        this.startDate = date;
    };

    @mobx.action
    public setTimezone = (timezone: string): void => {
        this.timezone = timezone;
    };

    private combineDateAndTime(date: Date, time: Date): Date {
        const datePart = format(utcToZonedTime(date, this.timezone), DateFnsFormats.FullDate).slice(0, 10);
        const timePart = format(utcToZonedTime(time, this.timezone), TimeFormats.HoursMinutesSeconds);
        return zonedTimeToUtc(`${datePart} ${timePart}`, this.timezone);
    }

    private init = (initialGoLiveDateTime: Date | null): void => {
        if (initialGoLiveDateTime) {
            this.isEnabled = true;
            this.goLiveDate = initialGoLiveDateTime;
            this.goLiveTime = initialGoLiveDateTime;
        } else {
            this.isEnabled = false;
        }
    };

    private prefillDateTimeWhenEnable = (): void => {
        if (this.isEnabled && !this.goLiveDate && !this.goLiveTime) {
            this.goLiveDate = this.meetingDate;
            this.goLiveTime = this.startDate;
        }
    };

    private clearDateTimeWhenDisable = () => {
        if (!this.isEnabled) {
            this.goLiveDate = null;
            this.goLiveTime = null;
        }
    };
}

export { MeetingGoLiveDateVM };
